import { useState, useMemo, useEffect } from 'react'
import { useEvent } from 'react-use'
import { rectContainsPoint } from 'utils/geometry'
import { Layer } from 'modules/shares/types'
import { unflattenLayers } from './layerTree'

// The useContextMenu hook prepares the state that the ContextMenu needs to have
// as props; It is responsible to build a tree of layers and calculate the
// layers available at a given (x,y) position; it takes zoom into consideration
// for the positioning.
// This hook also listens to the 'contextmenu' event and decides when to
// override it with our own layer menu.
interface ContextMenuHookOptions {
  layers?: { [key: string]: Layer }
  ref: React.RefObject<HTMLDivElement>
  zoom: number
}

interface ContextMenuState {
  menuX: number
  menuY: number
  visible: boolean
  layersAtPixel: Layer[]
}

// Calculates the place to put the menu; this is done in relation to the
// bounding rectangle of a given element (typically the artboard view element)
const menuPosition = (x: number, y: number, measures: DOMRect) => {
  const posX = x - Math.max(measures.left, 0) - Math.min(measures.x, 0)
  const posY = y - Math.max(measures.top, 0) - Math.min(measures.y, 0)
  return {
    menuX: isNaN(posX) ? 0 : posX,
    menuY: isNaN(posY) ? 0 : posY,
  }
}

// Finds the point in artboard space for a given page x,y position. Takes zoom
// and scroll into consideration.
const artboardPosition = (
  pageX: number,
  pageY: number,
  zoom: number,
  measures: DOMRect,
  element: HTMLElement | null
) => {
  let scrollX = 0
  let scrollY = 0
  if (element) {
    scrollX = element.scrollLeft
    scrollY = element.scrollTop
  }

  // The following position calculations assume that scroll always happens to
  // the right and to the bottom
  // This works well in zoomed-in artboards. Unfortunately this might have to
  // change if in the future we want to support artboard centering and
  // full scrolling in all quadrants when documents have very big layers
  // that trigger scrolling when zoomed-out.
  const x = (pageX + scrollX - (scrollX + measures.left)) / zoom
  const y = (pageY + scrollY - (scrollY + measures.top)) / zoom
  return { x, y }
}

export const useContextMenu = ({
  zoom,
  layers,
  ref,
}: ContextMenuHookOptions) => {
  const [
    { menuX, menuY, visible, layersAtPixel },
    setMenuState,
  ] = useState<ContextMenuState>({
    menuX: 0,
    menuY: 0,
    visible: false,
    layersAtPixel: [],
  })
  const [layersList, setLayersList] = useState<Layer[]>([])

  // Only build the tree of layers if there is a new set of layers to work on
  // this avoids rebuilding it at every render
  const trees = useMemo(() => {
    if (layers) {
      return unflattenLayers(layersAtPixel, layers)
    }
    return []
  }, [layers, layersAtPixel])

  // When the layers change transform them into a sorted array and set it
  // as state
  useEffect(() => {
    if (layers) {
      const layersArray = Object.values(layers).sort(
        (l1: Layer, l2: Layer) => (l2.order as number) - (l1.order as number)
      )
      setLayersList(layersArray)
    }
  }, [layers])

  // 'contextmenu' event handler, besides deciding when to show or not to show
  // the layer menu it also calculates the layers available at the given pixel
  // location. This way the layer intersection code is only run when the menu
  // is called and not at every render.
  const contextMenuHandler = (e: Event) => {
    if (!ref?.current) {
      return
    }
    const node = ref.current

    // our custom context menu should only be visible if the mouse event was
    // inside the artboard container, otherwise show the normal context menu
    if (node.contains(e.target as Node)) {
      e.preventDefault()
      const pageX = (e as MouseEvent).pageX || (e as CustomEvent)?.detail?.pageX
      const pageY = (e as MouseEvent).pageY || (e as CustomEvent)?.detail?.pageY
      const node = ref.current

      // Direct DOM reading is happening here inside the event handler
      // which is run out of the react render loop.
      const measures = node.getBoundingClientRect()
      const artboardElem = document.getElementById('ArtboardView')

      // Transforms the page x,y point into artboard coordinates and does the
      // intersection with the available layers.
      const artboardPt = artboardPosition(
        pageX,
        pageY,
        zoom,
        measures,
        artboardElem
      )
      const touchedLayers = layersList.filter(layer =>
        rectContainsPoint(layer.frame, artboardPt)
      )

      // Show the menu and set the information processed here to be shown
      setMenuState({
        visible: true,
        layersAtPixel: touchedLayers,
        ...menuPosition(pageX, pageY, measures),
      })
    }
  }

  useEvent('contextmenu', contextMenuHandler, ref?.current ?? document)

  // click outside must be specially dealt because of the inspector
  // hover/select layer on click
  useEvent(
    'click',
    e => {
      // Safari browser needs a special validation in order to avoid setting
      // the state bellow and hidding the menu right after showing it
      if ((e as MouseEvent).ctrlKey && visible) {
        return
      }

      // Defer setting the new state to allow the event to be propagated through.
      // Without defering, setting the state would immediately remove the menu
      // thus preventing the menu actions to be triggered if a click is
      // happening on them
      window.setTimeout(
        () =>
          setMenuState({
            menuX: -1,
            menuY: -1,
            visible: false,
            layersAtPixel: [],
          }),
        1
      )
    },
    document, // look for clicks everywhere
    { capture: true } // done on the capture phase to preempt hidding the menu
  )
  return [trees, menuX, menuY, visible] as const
}
